/**
 * Msg 560 · D.2 — LeftRailTree + StructuredAttributesPanel.
 *
 * The tree renders as a FLAT list of pre-computed rows (indent via
 * left padding) so we avoid recursive `<TreeNode>` self-render. That
 * self-render trips visual-edits/babel-plugin's variable-origin
 * tracer with a stack overflow.
 *
 * Msg 579 · Bug-1 — hide-non-ancestor-siblings filter. On focus (i.e.
 * after a navigation), only the ancestor path + current node + direct
 * children of current node render in the tree. Non-ancestor siblings
 * are excluded from the DOM entirely. The user can chevron-expand any
 * root or ancestor to reveal its full sibling list — that MANUAL
 * expansion sets an override flag on the node so its children render
 * unfiltered.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  ChevronRight, ChevronDown, Loader2, Home,
  Pencil, Trash2, Plus, Check, X as CloseIcon,
  CheckSquare, Square, Move as MoveIcon, FolderPlus,
  ArrowUpToLine, MoreVertical, Palette, AlertTriangle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

import { EntityIcon, humanize, TopicFormModal } from "./AtlasTopicShared";
import { useAtlasAttributes } from "./atlasAttributesCache";
import { MoveTopicModal } from "./AtlasTopicMove";
import FactSourceInfoBadge from "./FactSourceInfoBadge";
import VerificationBadge from "./AtlasVerificationBadge";
import { useAuth } from "@/context/AuthContext";
import {
  useAtlasCategories, invalidateCategories,
} from "./atlasCategoriesCache";
import { iconByName } from "./topicIconMap";


function flattenTree(roots, openSet, childrenCache, ancestorPathSet, manualOpenSet, currentSlug) {
  // Returns [{node, level, hasChildren}] visible under current open-set.
  //
  // Msg 579 filter — when descending into an AUTO-opened ancestor
  // parent, only render children that are themselves on the ancestor
  // path (or are the current node, or are manually opened). Direct
  // children of the current node are always rendered. Manually opened
  // (via chevron click) parents render children unfiltered.
  const out = [];
  const walk = (nodes, level, parentSlug) => {
    const filtered = nodes.filter((c) => {
      // Roots always render (parentSlug is null).
      if (parentSlug === null) return true;
      // If the parent is the current node, render every direct child.
      if (parentSlug === currentSlug) return true;
      // If the parent was manually chevron-expanded, render every child.
      if (manualOpenSet && manualOpenSet.has(parentSlug)) return true;
      // Otherwise we're descending into an auto-opened ancestor —
      // only include children on the ancestor path (or manually opened
      // ones so subtree expansion below still works).
      if (ancestorPathSet && ancestorPathSet.has(c.slug)) return true;
      if (manualOpenSet && manualOpenSet.has(c.slug)) return true;
      return false;
    });
    for (const n of filtered) {
      const kids = childrenCache[n.slug] || n.children || [];
      out.push({ node: n, level, hasChildren: kids.length > 0 || childrenCache[n.slug] === undefined });
      if (openSet.has(n.slug) && kids.length > 0) {
        walk(kids, level + 1, n.slug);
      }
    }
  };
  walk(roots, 0, null);
  return out;
}


// Msg 581 · K.2 — depth-based typographic scale + subtle background
// tint per generation, so the tree visually breaks up parents from
// grandchildren. Active node overrides the tint with the navy accent.
const _DEPTH_TYPO = [
  // depth 0: root
  { text: "text-base", weight: "font-bold",    tint: "bg-transparent",   icon: "w-4 h-4" },
  // depth 1
  { text: "text-sm",   weight: "font-semibold", tint: "bg-slate-50/60",   icon: "w-3.5 h-3.5" },
  // depth 2
  { text: "text-sm",   weight: "font-medium",  tint: "bg-slate-100/60",  icon: "w-3 h-3" },
  // depth 3+
  { text: "text-xs",   weight: "font-normal",  tint: "bg-slate-200/50",  icon: "w-3 h-3" },
];
function _depthTypo(level) {
  return _DEPTH_TYPO[Math.min(level, _DEPTH_TYPO.length - 1)];
}


function TreeRow({ row, isActive, isAncestor, isOpen, busy, onToggle, onNavigate,
                     selectMode = false, selected = false, onToggleSelect,
                     onMoveToTop, onMoveToFolder, onEdit, onBulkAdd, menuOpen, onMenuOpen, onMenuClose,
                     verification = null, isAdmin = false, onVerified }) {
  const typo = _depthTypo(row.level);
  const tone = isActive
    ? "bg-tb-navy text-tb-gold-bright"
    : isAncestor
      ? "bg-tb-navy/5 text-tb-navy"
      : `${typo.tint} text-tb-navy/85 hover:bg-tb-navy/[0.06]`;
  const handleRowClick = () => {
    if (selectMode) {
      onToggleSelect?.(row.node.slug);
      return;
    }
    // Msg 599 · P0 UX — clicking the row name should EXPAND the folder
    // (never collapse — the chevron handles collapse) in addition to
    // navigating to its focus page. Gives users a fat click target
    // that both drills in and reveals sub-folders in one motion.
    if (!isOpen) onToggle?.(row.node.slug, /* forceOpen */ true);
    onNavigate(row.node.slug);
  };
  // Msg 586 · Part B — "Move to top" only meaningful when this node
  // has a parent (level > 0). Root rows still get the kebab wrapper
  // for layout consistency but the menu item is disabled there.
  const canPin = row.level > 0;
  return (
    <div
      className={`group relative flex items-center gap-1 pr-2 rounded-lg cursor-pointer ${tone}`}
      style={{ paddingLeft: `${row.level * 14 + 4}px` }}
      data-testid={`tree-node-${row.node.slug}`}
      data-depth={row.level}
      data-selected={selected ? "1" : "0"}
    >
      {selectMode && (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(row.node.slug); }}
          data-testid={`tree-select-checkbox-${row.node.slug}`}
          aria-label={selected ? "Deselect" : "Select"}
          className={`h-5 w-5 shrink-0 rounded border inline-flex items-center justify-center ${
            selected
              ? "border-tb-navy bg-tb-navy text-tb-gold-bright"
              : (isActive ? "border-tb-gold-bright/60 text-tb-gold-bright" : "border-tb-navy/40 bg-white text-tb-navy")
          }`}>
          {selected ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
        </button>
      )}
      <button type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(row.node.slug); }}
        className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded hover:bg-black/5"
        aria-label={isOpen ? "Collapse" : "Expand"}
      >
        {busy
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : isOpen
            ? <ChevronDown className="w-3 h-3" />
            : <ChevronRight className="w-3 h-3" />}
      </button>
      <button type="button" onClick={handleRowClick}
        data-testid={`tree-nav-${row.node.slug}`}
        className="flex-1 min-w-0 flex items-center gap-1.5 h-8 text-left">
        <EntityIcon categoryId={row.node.category_id} className={`${typo.icon} shrink-0`} />
        <span className={`font-hand ${typo.text} ${typo.weight} truncate`}>{row.node.name}</span>
        {/* Verification badge (2026-08-08). Read-only for advisors,
            clickable for admins. Reads its state from the polled map
            in LeftRailTree — falls back to the row doc's own field
            on first render before polling completes. */}
        <VerificationBadge
          slug={row.node.slug}
          status={verification?.verification_status
                    || row.node.verification_status
                    || "unverified"}
          verifiedAt={verification?.verified_at || row.node.verified_at}
          verifiedByName={verification?.verified_by_name || row.node.verified_by_name}
          contentChangedAt={verification?.content_changed_at || row.node.content_changed_at}
          isAdmin={isAdmin}
          size="sm"
          onVerified={onVerified}
        />
        {/* Msg 605 · P0 · orphan badge. Backend flags rows whose
            `parent_slugs` points to a slug with no matching topic
            doc — invisible in the tree before this fix. The chip
            here surfaces them at the root level and hints at the
            missing parent so the user can Move → real parent. */}
        {row.node.is_orphan && (
          <span
            data-testid={`tree-orphan-badge-${row.node.slug}`}
            title={`This topic references parent "${row.node.orphan_of}" which no longer exists. Use Move to reparent.`}
            className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-hand bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wide">
            orphan
          </span>
        )}
      </button>
      {/* Msg 586 · Part B — per-row kebab menu with "Move to top". */}
      {!selectMode && (
        <div className="relative shrink-0">
          <button type="button"
            onClick={(e) => { e.stopPropagation(); menuOpen ? onMenuClose?.() : onMenuOpen?.(row.node.slug); }}
            data-testid={`tree-row-menu-${row.node.slug}`}
            aria-label="Row menu"
            className={`h-6 w-6 inline-flex items-center justify-center rounded hover:bg-black/10 opacity-0 group-hover:opacity-100 focus:opacity-100 ${menuOpen ? "opacity-100" : ""}`}>
            <MoreVertical className="w-3 h-3" />
          </button>
          {menuOpen && (
            <div
              data-testid={`tree-row-menu-panel-${row.node.slug}`}
              className="absolute z-40 right-0 top-full mt-1 min-w-[220px] rounded-lg border border-tb-navy/15 bg-white shadow-xl p-1"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button"
                disabled={!canPin}
                onClick={() => { onMoveToTop?.(row.node.slug); onMenuClose?.(); }}
                data-testid={`tree-row-menu-move-to-top-${row.node.slug}`}
                className="w-full inline-flex items-center gap-2 px-2 py-1.5 rounded text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-40 disabled:cursor-not-allowed text-left">
                <ArrowUpToLine className="w-3.5 h-3.5" />
                {canPin ? "Move to top" : "Move to top (root pins n/a)"}
              </button>
              <button type="button"
                onClick={() => { onMoveToFolder?.(row.node.slug); onMenuClose?.(); }}
                data-testid={`tree-row-menu-move-to-folder-${row.node.slug}`}
                className="w-full inline-flex items-center gap-2 px-2 py-1.5 rounded text-tb-navy font-hand text-xs hover:bg-tb-navy/5 text-left">
                <MoveIcon className="w-3.5 h-3.5" />
                Move to different folder…
              </button>
              {/* Msg 596 · Bug fix — Edit topic (opens the same
                  TopicFormModal used by the focus header). Lets an
                  advisor change the folder's Category / name / parent
                  without navigating into the folder first. */}
              <button type="button"
                onClick={() => { onEdit?.(row.node.slug); onMenuClose?.(); }}
                data-testid={`tree-row-menu-edit-${row.node.slug}`}
                className="w-full inline-flex items-center gap-2 px-2 py-1.5 rounded text-tb-navy font-hand text-xs hover:bg-tb-navy/5 text-left">
                <Pencil className="w-3.5 h-3.5" />
                Edit topic…
              </button>
              {/* (2026-08-04) Bulk add items with THIS folder pre-filled
                  as the parent. Deep-links to the /atlas/admin/bulk-add
                  page (which reads `parent_slug` from the query
                  string) — reuses the existing bulk-add UI instead of
                  duplicating it in a modal. */}
              <button type="button"
                onClick={() => { onBulkAdd?.(row.node.slug, row.node.name); onMenuClose?.(); }}
                data-testid={`tree-row-menu-bulk-add-${row.node.slug}`}
                className="w-full inline-flex items-center gap-2 px-2 py-1.5 rounded text-tb-navy font-hand text-xs hover:bg-tb-navy/5 text-left">
                <FolderPlus className="w-3.5 h-3.5" />
                Bulk add items…
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function LeftRailTree({ currentSlug, ancestorSlugs = [], onNavigate, refreshKey = 0, onExternalRefresh }) {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSet, setOpenSet] = useState(new Set(ancestorSlugs));
  // Msg 599 · P0 fix — mirror openSet into a ref so the roots-refetch
  // effect (which is triggered by nonce/refresh bumps, NOT by openSet
  // changes) can read the CURRENT openSet at fire time. Keeps that
  // effect's dep list minimal + immune to closure-races.
  const openSetRef = useRef(openSet);
  useEffect(() => { openSetRef.current = openSet; }, [openSet]);
  // Msg 579 — nodes the user MANUALLY chevron-expanded. When descending
  // into these, all children render unfiltered (siblings visible).
  const [manualOpenSet, setManualOpenSet] = useState(new Set());
  const [childrenCache, setChildrenCache] = useState({});
  const [loadingSlugs, setLoadingSlugs] = useState(new Set());
  // Msg 583 · Task M — bulk multi-select from the rail.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState(() => new Set());
  const [showMoveBulk, setShowMoveBulk] = useState(false);
  const [showSetCategory, setShowSetCategory] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  // Msg 586 · Part B — per-row kebab open state. Only one row can
  // have its menu open at a time; clicking anywhere else closes it.
  const [openMenuSlug, setOpenMenuSlug] = useState(null);
  // (2026-08-04) Repair Orphans modal — opens when the admin clicks
  // the "Repair Orphans" chip in the tree top bar. Visible only when
  // the current tree contains at least one `is_orphan: true` root.
  const [showRepairOrphans, setShowRepairOrphans] = useState(false);
  useEffect(() => {
    if (!openMenuSlug) return;
    const onDocClick = () => setOpenMenuSlug(null);
    window.addEventListener("click", onDocClick);
    return () => window.removeEventListener("click", onDocClick);
  }, [openMenuSlug]);
  // Internal refresh trigger — bumps when a bulk op finishes so the
  // outer refetch loop reruns. Also delegates to `onExternalRefresh`
  // when the parent wants to know (used by focus pages to bump their
  // own treeNonce).
  const [internalNonce, setInternalNonce] = useState(0);
  const bumpTree = useCallback(() => {
    setInternalNonce((n) => n + 1);
    try { onExternalRefresh?.(); } catch { /* noop */ }
  }, [onExternalRefresh]);
  const ancestorsKey = ancestorSlugs.join(",");

  // Msg 599 · P0 fix — preserve rail scroll position across refetches.
  // Without this, every mutation (Edit / Move-to-top / bulk apply)
  // snaps the rail back to top after the roots re-render.
  const scrollRef = useRef(null);
  const savedScrollRef = useRef(0);
  // Restore scroll AFTER internalNonce/refreshKey-driven re-renders
  // only. Running on every render caused a `setState`-in-effect loop
  // that surfaced as "Maximum update depth exceeded".
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (savedScrollRef.current > 0 && el.scrollTop !== savedScrollRef.current) {
      el.scrollTop = savedScrollRef.current;
    }
  }, [refreshKey, internalNonce]);

  useEffect(() => {
    setLoading(true);
    // Msg 599 · P0 fix — DO NOT wipe openSet / manualOpenSet here. The
    // user's expanded-node intent must survive a refetch (Move-to-top,
    // Edit, bulk apply, etc.) so their drill-down is preserved.
    //
    // Previous behaviour (Msg 576) was to hard-reset the trees on
    // every refresh — that's what caused "left-rail nav collapses to
    // root every time I hit Move-to-top or Edit".
    if (scrollRef.current) {
      savedScrollRef.current = scrollRef.current.scrollTop;
    }
    setLoadingSlugs(new Set());
    api.get("/atlas/topics/tree?depth=1")
      .then((r) => setRoots(r.data.tree || []))
      .finally(() => setLoading(false));
    // Also force-refetch children for every currently-expanded slug
    // so structural mutations (Move / rename / bulk create) surface
    // immediately. Read `openSet` at fire time (captured via ref) to
    // avoid the closure-race that surfaced as "children stayed
    // stale" on the previous attempt.
    const snapshot = openSetRef.current;
    snapshot.forEach(async (s) => {
      if (!s) return;
      try {
        const r = await api.get(`/atlas/topics/${s}/children`);
        setChildrenCache((prev) => ({ ...prev, [s]: r.data.children || [] }));
      } catch { /* noop */ }
    });
  }, [refreshKey, internalNonce]);

  useEffect(() => {
    // Msg 574 · UX-Cleanup — focus mode. On every navigation, EXPAND
    // the ancestor chain + current slug so drilling down never leaves
    // the target node collapsed. Msg 599 · P0 fix — we UNION with the
    // existing openSet (was: replace) so manually-expanded branches
    // survive navigation between siblings and cousins. Collapse still
    // works via the chevron; nothing auto-collapses now.
    setOpenSet((prev) => {
      // Only rebuild the Set if there's actually something new to add.
      // Rebuilding a same-content Set still changes its reference and
      // was cascading with the rows useMemo into a "Maximum update
      // depth exceeded" loop on some navigation transitions.
      let hasNew = false;
      for (const s of ancestorSlugs) if (!prev.has(s)) { hasNew = true; break; }
      if (!hasNew && (currentSlug ? prev.has(currentSlug) : true)) return prev;
      const next = new Set(prev);
      for (const s of ancestorSlugs) next.add(s);
      if (currentSlug) next.add(currentSlug);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancestorsKey, currentSlug]);

  useEffect(() => {
    // Only fetch children for slugs whose cache is missing. On nonce
    // bump the effect above already force-refetches every currently-
    // expanded slug via the openSetRef snapshot; here we just cover
    // the normal navigation case (ancestorsKey / currentSlug change).
    const slugsToLoad = new Set([...ancestorSlugs, ...openSet]);
    if (currentSlug) slugsToLoad.add(currentSlug);
    const missing = [...slugsToLoad].filter((s) => s && !childrenCache[s]);
    if (missing.length === 0) return;
    let cancelled = false;
    missing.forEach(async (s) => {
      try {
        const r = await api.get(`/atlas/topics/${s}/children`);
        if (cancelled) return;
        setChildrenCache((prev) => ({ ...prev, [s]: r.data.children || [] }));
      } catch { /* noop */ }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancestorsKey, currentSlug]);

  const ensureChildren = useCallback(async (slug) => {
    if (childrenCache[slug]) return;
    setLoadingSlugs((prev) => new Set([...prev, slug]));
    try {
      const r = await api.get(`/atlas/topics/${slug}/children`);
      setChildrenCache((prev) => ({ ...prev, [slug]: r.data.children || [] }));
    } finally {
      setLoadingSlugs((prev) => { const n = new Set(prev); n.delete(slug); return n; });
    }
  }, [childrenCache]);

  const toggle = useCallback((slug, forceOpen = false) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      // Msg 599 · P0 UX — when called from the row body click, we
      // always OPEN (never collapse). Chevron clicks still toggle.
      if (forceOpen) {
        if (!next.has(slug)) {
          next.add(slug);
          ensureChildren(slug);
          setManualOpenSet((mprev) => {
            const m = new Set(mprev); m.add(slug); return m;
          });
        }
        return next;
      }
      if (next.has(slug)) {
        // Collapse — also drop any manual-open flag.
        next.delete(slug);
        setManualOpenSet((mprev) => {
          if (!mprev.has(slug)) return mprev;
          const m = new Set(mprev); m.delete(slug); return m;
        });
      } else {
        // Manual expand — record as manually opened so children render
        // unfiltered (Bug-1: chevron on an ancestor reveals siblings).
        next.add(slug);
        ensureChildren(slug);
        setManualOpenSet((mprev) => {
          const m = new Set(mprev); m.add(slug); return m;
        });
      }
      return next;
    });
  }, [ensureChildren]);

  const ancestorPathSet = useMemo(() => {
    const s = new Set(ancestorSlugs);
    if (currentSlug) s.add(currentSlug);
    return s;
  }, [ancestorSlugs, currentSlug]);

  const rows = useMemo(
    () => flattenTree(roots, openSet, childrenCache, ancestorPathSet, manualOpenSet, currentSlug),
    [roots, openSet, childrenCache, ancestorPathSet, manualOpenSet, currentSlug],
  );

  // Verification badges (2026-08-08). Poll the visible slugs every
  // 20 s so other admins' verify clicks (or ingestion applies that
  // flipped a folder to needs_review) propagate across sessions.
  // Map is keyed by slug → { verification_status, verified_at, ... }.
  const { user } = useAuth();
  const isAtlasAdmin = Boolean(user?.roles?.includes?.("atlas_admin"));
  const [verifState, setVerifState] = useState({});
  const visibleSlugsKey = useMemo(
    () => rows.map((r) => r.node.slug).sort().join(","),
    [rows],
  );
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchOnce = async () => {
      const slugs = visibleSlugsKey;
      if (!slugs) return;
      try {
        const { data } = await api.get(
          `/atlas/topics/verification-status?slugs=${encodeURIComponent(slugs)}`);
        if (cancelled) return;
        const next = {};
        for (const it of data?.items || []) next[it.slug] = it;
        setVerifState(next);
      } catch {
        // Silent — badge just uses the stale map on next paint.
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, 20000);
    // Also refresh when the tab regains focus so long-idle rails
    // catch up immediately.
    const onVis = () => { if (document.visibilityState === "visible") fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [visibleSlugsKey]);
  const handleVerifyLocally = useCallback((slug, fresh) => {
    setVerifState((prev) => ({ ...prev, [slug]: { slug, ...fresh } }));
  }, []);

  const toggleSelect = useCallback((slug) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedSlugs(new Set());
    setShowMoveBulk(false);
    setShowCreateGroup(false);
  }, []);

  // Msg 586 · Part B — "Move to top" handler. Calls the atomic
  // backend endpoint then bumps the tree via `bumpTree()` so the
  // pin propagates to every consumer (rail + focus grids).
  const handleMoveToTop = useCallback(async (targetSlug) => {
    try {
      const r = await api.post(`/atlas/topics/${targetSlug}/move-to-top`);
      const priority = r.data?.sort_priority ?? "?";
      toast.success(`Pinned to top (priority ${priority}).`);
      bumpTree();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Move to top failed.");
    }
  }, [bumpTree]);

  // Msg 587 · P0 fix — "Move to different folder" from the rail kebab.
  // Opens the existing MoveTopicModal (rich search + expandable tree
  // + Make-root + cycle prevention). Fetches topic + ancestors so the
  // modal can pre-expand the ancestor chain and highlight the current
  // parent.
  const [moveTarget, setMoveTarget] = useState(null); // { topic, ancestors }
  const handleMoveToFolder = useCallback(async (targetSlug) => {
    try {
      const [topicRes, ancRes] = await Promise.all([
        api.get(`/atlas/topics/${targetSlug}`),
        api.get(`/atlas/topics/${targetSlug}/ancestors`),
      ]);
      setMoveTarget({
        topic: topicRes.data?.topic || { slug: targetSlug, name: targetSlug },
        ancestors: ancRes.data?.ancestors || [],
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't open Move dialog.");
    }
  }, []);

  // Msg 596 · Bug fix — "Edit topic…" from the rail kebab. Opens the
  // same TopicFormModal the focus header uses, so an advisor can
  // change the folder's Category / name / parent from anywhere in the
  // tree (not only after navigating into the focus page). Parent
  // candidates exclude the target + its descendants to prevent cycle
  // formation via the parent picker.
  const [editTarget, setEditTarget] = useState(null); // { topic, parentCandidates }
  const handleEdit = useCallback(async (targetSlug) => {
    try {
      const topicRes = await api.get(`/atlas/topics/${targetSlug}`);
      const topic = topicRes.data?.topic;
      if (!topic) {
        toast.error("Topic not found.");
        return;
      }
      // Build parent candidates: every topic except the target and
      // its descendants. Mirrors AtlasTopicDetailPage.openEdit().
      const listRes = await api.get("/atlas/topics?limit=500");
      const list = listRes.data?.topics || [];
      const descendants = new Set([targetSlug]);
      const queue = [targetSlug];
      while (queue.length > 0) {
        const cur = queue.shift();
        try {
          const cr = await api.get(`/atlas/topics/${cur}/children`);
          for (const k of (cr.data.children || [])) {
            if (!descendants.has(k.slug)) {
              descendants.add(k.slug);
              queue.push(k.slug);
            }
          }
        } catch { /* noop */ }
      }
      setEditTarget({
        topic,
        parentCandidates: list.filter((p) => !descendants.has(p.slug)),
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't open Edit dialog.");
    }
  }, []);

  // (2026-08-04) Kebab → "Bulk add items…" — deep-links to the bulk-add
  // admin page with `parent_slug` + `parent_name` pre-filled via query
  // string. Uses window.location instead of `useNavigate` to avoid
  // adding another router dependency to the tree component; the target
  // page reads the params on mount.
  const handleBulkAdd = useCallback((slug, name) => {
    try {
      const params = new URLSearchParams();
      if (slug) params.set("parent_slug", slug);
      if (name) params.set("parent_name", name);
      window.location.href = `/atlas/admin/bulk-add?${params.toString()}`;
    } catch (e) {
      toast.error("Couldn't open Bulk add items.");
    }
  }, []);

  return (
    <div className="hidden md:flex md:w-60 lg:w-64 shrink-0 border-r border-tb-navy/10 pr-2 py-4 sticky top-0 self-start h-[calc(100vh-84px)]"
         data-testid="atlas-tree-rail">
      <div ref={scrollRef}
           className="flex-1 min-h-0 overflow-y-auto pb-16 relative"
           data-testid="atlas-tree-rail-scroll">
        <div className="px-2 mb-2 flex items-center justify-between gap-1">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">Topics tree</span>
          <div className="inline-flex items-center gap-1">
            {/* (2026-08-04) Repair Orphans — only when the tree has any
                is_orphan pseudo-root. Chip is amber so it stands out. */}
            {roots.some((r) => r.is_orphan) && (
              <button type="button"
                onClick={() => setShowRepairOrphans(true)}
                data-testid="atlas-tree-repair-orphans-btn"
                title="Reparent orphan sub-folders whose parent no longer exists"
                className="h-6 px-2 inline-flex items-center gap-1 rounded font-hand text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200">
                <AlertTriangle className="w-3 h-3" />
                Repair {roots.filter((r) => r.is_orphan).length}
              </button>
            )}
            {/* Msg 583 · Task M — Select-mode toggle button. */}
            <button type="button"
              onClick={() => {
                if (selectMode) exitSelectMode();
                else setSelectMode(true);
              }}
              data-testid="atlas-tree-select-toggle"
              title={selectMode ? "Cancel selection" : "Select topics to move or group"}
              className={`h-6 px-1.5 inline-flex items-center gap-1 rounded font-hand text-[10px] uppercase tracking-widest transition-colors ${
                selectMode
                  ? "bg-tb-navy text-tb-gold-bright"
                  : "text-tb-navy/60 hover:bg-tb-navy/5"
              }`}>
              {selectMode
                ? <><CloseIcon className="w-3 h-3" />Cancel</>
                : <><CheckSquare className="w-3 h-3" />Select</>}
            </button>
            <button type="button" onClick={() => onNavigate("")}
              data-testid="tree-nav-root"
              className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-tb-navy/5 text-tb-navy/60"
              title="Back to landing">
              <Home className="w-3 h-3" />
            </button>
          </div>
        </div>
        {loading && (
          <div className="px-3 py-2 font-hand text-tb-navy/40 text-[11px]">Loading tree…</div>
        )}
        {!loading && roots.length === 0 && (
          <div className="px-3 py-2 font-hand text-tb-navy/40 text-[11px]">No topics yet.</div>
        )}
        {!loading && rows.map((row) => (
          <TreeRow
            key={row.node.slug}
            row={row}
            isActive={row.node.slug === currentSlug}
            isAncestor={ancestorSlugs.includes(row.node.slug)}
            isOpen={openSet.has(row.node.slug)}
            busy={loadingSlugs.has(row.node.slug)}
            onToggle={toggle}
            onNavigate={onNavigate}
            selectMode={selectMode}
            selected={selectedSlugs.has(row.node.slug)}
            onToggleSelect={toggleSelect}
            onMoveToTop={handleMoveToTop}
            onMoveToFolder={handleMoveToFolder}
            onEdit={handleEdit}
            onBulkAdd={handleBulkAdd}
            menuOpen={openMenuSlug === row.node.slug}
            onMenuOpen={(s) => setOpenMenuSlug(s)}
            onMenuClose={() => setOpenMenuSlug(null)}
            verification={verifState[row.node.slug] || null}
            isAdmin={isAtlasAdmin}
            onVerified={(fresh) => handleVerifyLocally(row.node.slug, fresh)}
          />
        ))}
      </div>

      {/* Msg 583 · Task M — Floating action bar when >=1 selected. */}
      {selectMode && selectedSlugs.size > 0 && (
        <div
          data-testid="atlas-tree-bulk-action-bar"
          className="absolute bottom-2 left-2 right-2 rounded-2xl border border-tb-navy/15 bg-white shadow-xl p-2 flex flex-col gap-1"
        >
          <div className="font-hand text-[11px] text-tb-navy/70 px-1">
            <span data-testid="atlas-tree-bulk-count">{selectedSlugs.size}</span> selected
            {selectedSlugs.size > 200 && (
              <span className="ml-1 text-red-700">(over 200 — consider fewer)</span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <button type="button"
              onClick={() => setShowMoveBulk(true)}
              data-testid="atlas-tree-bulk-move"
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-lg bg-tb-navy text-tb-gold-bright font-hand text-xs">
              <MoveIcon className="w-3 h-3" /> Move to…
            </button>
            <button type="button"
              onClick={() => setShowSetCategory(true)}
              data-testid="atlas-tree-bulk-set-category"
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-lg border border-tb-navy/25 bg-white text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
              <Palette className="w-3 h-3" /> Set category…
            </button>
            <button type="button"
              onClick={() => setShowCreateGroup(true)}
              data-testid="atlas-tree-bulk-group"
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-lg border border-tb-navy/25 bg-white text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
              <FolderPlus className="w-3 h-3" /> Create parent group…
            </button>
            <button type="button"
              onClick={exitSelectMode}
              data-testid="atlas-tree-bulk-cancel"
              className="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg text-tb-navy/60 font-hand text-[11px] hover:bg-tb-navy/5">
              <CloseIcon className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Msg 583 · Task M — reuse existing MoveTopicModal in bulk mode. */}
      {showMoveBulk && selectedSlugs.size > 0 && (
        <MoveTopicModal
          open={showMoveBulk}
          topic={null}
          bulkSlugs={Array.from(selectedSlugs)}
          onClose={() => setShowMoveBulk(false)}
          onMoved={() => {
            setShowMoveBulk(false);
            exitSelectMode();
            bumpTree();
          }}
        />
      )}

      {/* Msg 595 — Bulk set-category modal. */}
      {showSetCategory && selectedSlugs.size > 0 && (
        <BulkSetCategoryModal
          slugs={Array.from(selectedSlugs)}
          onClose={() => setShowSetCategory(false)}
          onDone={() => {
            setShowSetCategory(false);
            exitSelectMode();
            invalidateCategories();
            bumpTree();
          }}
        />
      )}

      {showCreateGroup && selectedSlugs.size > 0 && (
        <CreateParentGroupModal
          selectedSlugs={Array.from(selectedSlugs)}
          onCancel={() => setShowCreateGroup(false)}
          onDone={() => {
            setShowCreateGroup(false);
            exitSelectMode();
            bumpTree();
          }}
        />
      )}

      {/* Msg 587 · P0 fix — Move-to-different-folder dialog invoked
          from the per-row kebab. Reuses AtlasTopicMove's MoveTopicModal
          which already ships the search + expandable tree + Make-root
          + cycle prevention behaviour the user was missing. */}
      {moveTarget && (
        <MoveTopicModal
          open={true}
          topic={moveTarget.topic}
          ancestors={moveTarget.ancestors}
          onClose={() => setMoveTarget(null)}
          onMoved={() => { setMoveTarget(null); bumpTree(); }}
        />
      )}

      {/* Msg 596 · Bug fix — Edit-topic dialog invoked from the per-row
          kebab. Reuses the SAME TopicFormModal the focus header uses so
          the Category dropdown, parent picker, and apply-template
          affordances are identical in both flows. */}
      {editTarget && (
        <TopicFormModal
          mode="edit"
          open={true}
          initial={editTarget.topic}
          parentCandidates={editTarget.parentCandidates}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidateCategories();
            bumpTree();
          }}
        />
      )}
      {showRepairOrphans && (
        <RepairOrphansModal
          onClose={() => setShowRepairOrphans(false)}
          onCommitted={() => { setShowRepairOrphans(false); bumpTree(); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Msg 583 · Task M — "+ Create parent group with selected".
// ─────────────────────────────────────────────────────────────
function CreateParentGroupModal({ selectedSlugs, onCancel, onDone }) {
  const [name, setName] = useState("");
  const [placeUnder, setPlaceUnder] = useState("");
  const [candidates, setCandidates] = useState([]); // [{slug, name}]
  const [defaultParent, setDefaultParent] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Compute closest common ancestor. For each selected slug, fetch
  // ancestors — then intersect from the root down and keep the deepest
  // match. Also collect the union of all ancestor slugs (as candidate
  // parents the advisor can override to). Selected topics themselves
  // are excluded as candidates.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      const chains = [];
      const allAncestorSlugs = new Set();
      const ancestorMeta = new Map();
      const selectedSet = new Set(selectedSlugs);
      try {
        for (const s of selectedSlugs) {
          const r = await api.get(`/atlas/topics/${s}/ancestors`);
          const chain = r.data?.ancestors || [];
          chains.push(chain.map((a) => a.slug));
          for (const a of chain) {
            if (selectedSet.has(a.slug)) continue;
            allAncestorSlugs.add(a.slug);
            ancestorMeta.set(a.slug, a.name || a.slug);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't compute ancestors.");
      }
      if (cancelled) return;
      // Longest common prefix across chains.
      let common = "";
      if (chains.length > 0) {
        const first = chains[0];
        for (let i = 0; i < first.length; i++) {
          const s = first[i];
          if (chains.every((c) => c[i] === s)) common = s; else break;
        }
      }
      const cand = Array.from(allAncestorSlugs).map((sl) => ({
        slug: sl, name: ancestorMeta.get(sl) || sl,
      }));
      // Also allow "root" as a placement.
      cand.unshift({ slug: "", name: "— Top level (no parent) —" });
      setCandidates(cand);
      setDefaultParent(common || "");
      setPlaceUnder(common || "");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [selectedSlugs]);

  const submit = async () => {
    const nm = name.trim();
    if (!nm) { setErr("Give the new group a name."); return; }
    setBusy(true); setErr("");
    try {
      // 1) Create the new topic.
      const createBody = { name: nm };
      if (placeUnder) createBody.parent_slug = placeUnder;
      const cr = await api.post("/atlas/topics", createBody);
      const newSlug = cr.data?.topic?.slug;
      if (!newSlug) throw new Error("Create returned no slug.");
      // 2) Reparent each selected topic to the new group.
      const results = await Promise.allSettled(
        selectedSlugs.map((s) => api.put(
          `/atlas/topics/${s}`, { parent_slug: newSlug }))
      );
      const moved = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - moved;
      if (failed > 0) {
        toast.warning(`Created "${nm}". Moved ${moved} of ${results.length} topics — ${failed} failed.`);
      } else {
        toast.success(`Created "${nm}" and moved ${moved} topic${moved === 1 ? "" : "s"} into it.`);
      }
      onDone?.();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Create + move failed.");
    } finally { setBusy(false); }
  };

  const disabled = busy || loading || !name.trim();
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-tree-create-group-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15 p-5">
        <h2 className="text-tb-navy text-lg font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}>
          Create a new parent group
        </h2>
        <p className="mt-1 font-hand text-tb-navy/60 text-xs">
          Creates a new topic and moves the {selectedSlugs.length} selected
          topic{selectedSlugs.length === 1 ? "" : "s"} into it.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/50">Group name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. North East US"
              autoFocus
              data-testid="atlas-tree-create-group-name"
              className="mt-1 w-full h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy" />
          </div>
          <div>
            <label className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/50">Place group under</label>
            <select value={placeUnder}
              onChange={(e) => setPlaceUnder(e.target.value)}
              disabled={loading}
              data-testid="atlas-tree-create-group-parent"
              className="mt-1 w-full h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
              {loading && <option value="">Loading…</option>}
              {!loading && candidates.map((c) => (
                <option key={c.slug || "__ROOT__"} value={c.slug}>
                  {c.name}{c.slug && c.slug === defaultParent ? "  · closest common ancestor" : ""}
                </option>
              ))}
            </select>
            <div className="mt-1 font-hand text-[11px] text-tb-navy/50">
              Default is the closest common ancestor of the selected topics. Override with any ancestor from the list.
            </div>
          </div>
          {err && (
            <div data-testid="atlas-tree-create-group-error"
                 className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 font-hand text-[11px] text-red-800">
              {err}
            </div>
          )}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="atlas-tree-create-group-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={disabled}
            data-testid="atlas-tree-create-group-submit"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
            Create + Move
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── StructuredAttributesPanel — full C.2 spec sheet ───
// Msg 572 · Phase I.3 — Unified table pattern. Every schema attribute
// (comparison-schema + template-defined) renders as a row.
// Msg 574 · UX-Cleanup — Advisors can also add CUSTOM attributes below
// the schema rows. Every attribute row (schema-defined or custom) has
// the same edit + delete affordances, no visual differentiation. Adding
// a custom attribute optionally broadcasts it to all topics under an
// ancestor scope (only_missing = never overwrites).
export function StructuredAttributesPanel({ slug }) {
  const [schema, setSchema] = useState({ entity_type: "other", attributes: [], template_attributes_count: 0 });
  const [rows, setRows] = useState([]);
  // 2026-08-10 · Prod-post-mortem — singular attribute delete leaves
  // a ghost placeholder when the attribute is defined at the ENTITY-
  // TYPE template library level (not the topic's own
  // `custom_attribute_schema`). Reload re-emits the template def and
  // combinedRows re-renders an empty placeholder, so the advisor
  // sees the row visually persist even though the fact + local
  // schema entry are gone. Mirrors the `atlasAttributesCache.js`
  // `removeAttributeById` fix from the Global Attributes Library.
  // The set is session-scoped (cleared on slug change / hard refresh)
  // — permanent per-topic suppression would need a new backend
  // "hidden_attribute_keys" field on atlas_topics.
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editStr, setEditStr] = useState("");
  const [editBool, setEditBool] = useState(false);
  const [editList, setEditList] = useState([]);
  const [editListDraft, setEditListDraft] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  // 2026-08-09 — bulk-select + mass-delete state. Admin-only. Row
  // checkboxes render only when `isAtlasAdmin` is true.
  const { user: authUser } = useAuth();
  const isAtlasAdmin = Boolean(authUser?.roles?.includes?.("atlas_admin"));
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkRemoveSchema, setBulkRemoveSchema] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  // Custom-add inline form state.
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("string");
  const [newValueStr, setNewValueStr] = useState("");
  const [newValueBool, setNewValueBool] = useState(false);
  const [newValueList, setNewValueList] = useState([]);
  const [newValueListDraft, setNewValueListDraft] = useState("");
  const [newAddError, setNewAddError] = useState("");
  const [newBusy, setNewBusy] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [applyScopeChecked, setApplyScopeChecked] = useState(false);
  const [applyScopeSlug, setApplyScopeSlug] = useState("");
  // Msg 582 · L.2 — "specific topics" cherry-pick mode. When
  // `applyMode==='specific'`, `applyTargetSlugs` holds the checked
  // slugs and the submit sends `target_slugs` to the backend which
  // restricts application to only those.
  const [applyMode, setApplyMode] = useState("all"); // "all" | "specific"
  const [applyTargetSlugs, setApplyTargetSlugs] = useState([]);
  // Msg 576 · delete-with-siblings modal state.
  const [deleteModal, setDeleteModal] = useState(null); // { row } | null
  const [deleteScopeChecked, setDeleteScopeChecked] = useState(false);
  const [deleteScopeSlug, setDeleteScopeSlug] = useState("");
  const [deleteMode, setDeleteMode] = useState("all");
  const [deleteTargetSlugs, setDeleteTargetSlugs] = useState([]);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/atlas/topics/${slug}/attribute-schema`).catch(() => ({ data: { entity_type: "other", attributes: [] } })),
      api.get(`/atlas/topics/${slug}/attributes`).catch(() => ({ data: { attributes: [] } })),
      api.get(`/atlas/topics/${slug}/ancestor-scopes`).catch(() => ({ data: { scopes: [] } })),
    ]).then(([sR, lR, scR]) => {
      setSchema({
        entity_type: sR.data?.entity_type || "other",
        attributes: sR.data?.attributes || [],
        template_attributes_count: sR.data?.template_attributes_count || 0,
      });
      setRows(lR.data?.attributes || []);
      const scopeList = scR.data?.scopes || [];
      setScopes(scopeList);
      // Default the scope selector to the direct parent (index 0).
      setApplyScopeSlug(scopeList[0]?.slug || "");
    }).finally(() => setLoading(false));
  }, [slug]);
  useEffect(reload, [reload]);
  // 2026-08-10 · Reset the hidden-keys session set whenever we
  // navigate to a different folder — the set is per-slug context.
  useEffect(() => { setHiddenKeys(new Set()); }, [slug]);

  const filledByKey = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.attribute_key, r);
    return m;
  }, [rows]);
  const schemaKeys = useMemo(
    () => new Set((schema.attributes || []).map((a) => a.key)),
    [schema.attributes],
  );
  const combinedRows = useMemo(() => {
    const out = [];
    const hidden = hiddenKeys;
    const isHidden = (k) => hidden.has(String(k || "").trim().toLowerCase());
    for (const def of schema.attributes || []) {
      if (isHidden(def.key)) continue;
      const filled = filledByKey.get(def.key);
      if (filled) {
        out.push({ ...filled, def, empty: false, custom: false });
      } else {
        out.push({
          def,
          attribute_key: def.key,
          attribute_value: null,
          empty: true, custom: false,
        });
      }
    }
    // Msg 574 — filled facts not in schema are custom advisor rows;
    // use the fact's own attribute_label / attribute_type when present.
    for (const r of rows) {
      if (isHidden(r.attribute_key)) continue;
      if (!schemaKeys.has(r.attribute_key)) {
        out.push({
          ...r,
          def: {
            key: r.attribute_key,
            label: r.attribute_label || humanize(r.attribute_key),
            type: r.attribute_type || "string",
            examples: [],
          },
          empty: false,
          custom: r.source === "advisor_custom" || !r.attribute_label ? true : true,
        });
      }
    }
    // Item 4 (2026-08-08) — Sort attributes case-insensitive by label
    // so the topic-page list is consistent with the alphabetical order
    // used on compare cards.
    out.sort((a, b) => {
      const la = String(a.def?.label || a.attribute_key || "").toLowerCase();
      const lb = String(b.def?.label || b.attribute_key || "").toLowerCase();
      return la.localeCompare(lb);
    });
    return out;
  }, [schema.attributes, filledByKey, rows, schemaKeys, hiddenKeys]);

  const startEdit = (row) => {
    const def = row.def;
    setEditingKey(row.attribute_key);
    setEditLabel(row.custom ? (row.attribute_label || def.label || humanize(row.attribute_key)) : "");
    if (def.type === "bool") {
      setEditBool(String(row.attribute_value || "").toLowerCase() === "true");
      setEditStr(""); setEditList([]);
    } else if (def.type === "list") {
      setEditList((row.attribute_value || "").split("|").map((s) => s.trim()).filter(Boolean));
      setEditStr(""); setEditBool(false);
    } else {
      setEditStr(row.attribute_value || "");
      setEditBool(false); setEditList([]);
    }
    setEditListDraft("");
  };

  const serializeEdit = (def) => {
    if (!def) return "";
    if (def.type === "bool") return editBool ? "true" : "false";
    if (def.type === "list") {
      const items = [...editList];
      const draft = (editListDraft || "").trim();
      if (draft) items.push(draft);
      return items.join(" | ");
    }
    return (editStr || "").trim();
  };

  const commitEdit = async (row) => {
    const def = row.def;
    const value = serializeEdit(def);
    if (!value) { setEditingKey(null); return; }
    const labelChanged = row.custom && (editLabel || "").trim() !== (row.attribute_label || "");
    if (!row.empty && value === row.attribute_value && !labelChanged) {
      setEditingKey(null); return;
    }
    setSavingKey(row.attribute_key);
    try {
      if (row.empty) {
        await api.post(`/atlas/topics/${slug}/attributes`, {
          attribute_key: row.attribute_key, attribute_value: value,
          source: "advisor_typed",
        });
        toast.success("Attribute saved.");
      } else {
        const body = { attribute_value: value };
        if (row.custom) body.attribute_label = (editLabel || "").trim();
        await api.put(`/atlas/topics/${slug}/attributes/${row.attribute_key}`, body);
        toast.success("Attribute updated.");
      }
      setEditingKey(null); reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing) {
        try {
          await api.put(`/atlas/topics/${slug}/attributes/${row.attribute_key}`, { attribute_value: value });
          toast.success("Attribute updated (overwrote concurrent write).");
          setEditingKey(null); reload();
          return;
        } catch (err2) {
          toast.error(formatApiErrorDetail(err2?.response?.data?.detail) || "Update failed.");
        }
      } else {
        toast.error(formatApiErrorDetail(detail) || "Save failed.");
      }
    } finally { setSavingKey(null); }
  };

  const removeRow = (row) => {
    // Msg 576 — open the delete-with-siblings modal instead of a
    // window.confirm. The modal offers an optional "Also remove from
    // all topics under: <scope>" checkbox mirroring the add flow.
    setDeleteScopeChecked(false);
    setDeleteScopeSlug(scopes[0]?.slug || "");
    setDeleteMode("all");
    setDeleteTargetSlugs([]);
    setDeleteModal({ row });
  };

  const doDelete = async () => {
    const row = deleteModal?.row;
    if (!row) return;
    const chosenScope = deleteScopeChecked
      ? scopes.find((s) => s.slug === deleteScopeSlug) : null;
    const specificMode = deleteScopeChecked && deleteMode === "specific";
    const targetSlugs = specificMode ? deleteTargetSlugs : null;
    if (specificMode && (!targetSlugs || targetSlugs.length === 0)) {
      toast.error("Pick at least one topic to remove the attribute from, or switch back to 'All topics under…'.");
      return;
    }
    if (chosenScope && !specificMode && chosenScope.descendants_count > 20) {
      const ok = window.confirm(
        `This will remove "${row.def?.label || row.attribute_key}" from ${chosenScope.descendants_count} topics under ${chosenScope.name}. Continue?`);
      if (!ok) return;
    }
    setDeleteBusy(true);
    setSavingKey(row.attribute_key);
    try {
      await api.delete(`/atlas/topics/${slug}/attributes/${row.attribute_key}`);
      let extraToast = "";
      if (chosenScope) {
        const rmBody = { attribute_key: row.attribute_key, only_present: true };
        if (targetSlugs) rmBody.target_slugs = targetSlugs;
        const r = await api.post(
          `/atlas/topics/${chosenScope.slug}/remove-attribute-from-descendants`,
          rmBody);
        const removed = r.data?.removed || 0;
        const skipped = r.data?.skipped_missing || 0;
        const scopeLbl = specificMode
          ? `${targetSlugs.length} picked topic${targetSlugs.length === 1 ? "" : "s"}`
          : `${removed === 1 ? "topic" : "topics"} under ${chosenScope.name}`;
        extraToast = ` Also removed from ${removed} ${scopeLbl}` +
          (skipped ? ` (${skipped} didn't have a value, skipped)` : "") + ".";
      }
      const label = row.def?.label || humanize(row.attribute_key);
      // 2026-08-10 · Optimistic splice — see `hiddenKeys` comment on
      // useState. Immediately remove the row from local state so the
      // advisor sees it disappear on the same tick the toast fires.
      // The subsequent `reload()` re-syncs `rows` + `schema` with the
      // server; `hiddenKeys` keeps the row masked even if the entity-
      // type template library still returns its definition.
      const deletedKey = String(row.attribute_key || "").trim().toLowerCase();
      setRows((prev) => prev.filter(
        (r) => String(r.attribute_key || "").trim().toLowerCase() !== deletedKey));
      setSchema((prev) => ({
        ...prev,
        attributes: (prev.attributes || []).filter(
          (a) => String(a.key || "").trim().toLowerCase() !== deletedKey),
      }));
      setHiddenKeys((prev) => {
        const next = new Set(prev);
        next.add(deletedKey);
        return next;
      });
      toast.success(`Removed '${label}'.${extraToast}`);
      setDeleteModal(null);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    } finally {
      setDeleteBusy(false);
      setSavingKey(null);
    }
  };

  // ─── Custom-add helpers ─────────────────────────────────────────
  const resetAddForm = () => {
    setNewLabel(""); setNewType("string");
    setNewValueStr(""); setNewValueBool(false);
    setNewValueList([]); setNewValueListDraft("");
    setNewAddError(""); setApplyScopeChecked(false);
    setApplyScopeSlug(scopes[0]?.slug || "");
    setApplyMode("all"); setApplyTargetSlugs([]);
  };

  const derivedNewKey = useMemo(
    () => (newLabel || "").toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60),
    [newLabel],
  );

  const serializeNewValue = () => {
    if (newType === "bool") return newValueBool ? "true" : "false";
    if (newType === "list") {
      const items = [...newValueList];
      const draft = (newValueListDraft || "").trim();
      if (draft) items.push(draft);
      return items.join(" | ");
    }
    return (newValueStr || "").trim();
  };

  const canSubmitCustom = derivedNewKey.length > 0 && (
    newType === "bool" ? true :
    newType === "list" ? (newValueList.length > 0 || (newValueListDraft || "").trim().length > 0) :
    (newValueStr || "").trim().length > 0
  );

  const submitCustom = async () => {
    if (!canSubmitCustom) return;
    const label = newLabel.trim();
    const value = serializeNewValue();
    const key = derivedNewKey;
    // Confirmation prompt if broadcasting to >20 topics.
    const chosenScope = applyScopeChecked
      ? scopes.find((s) => s.slug === applyScopeSlug) : null;
    // Msg 582 · L.2 — "specific topics" cherry-pick. When mode is
    // "specific" we send `target_slugs`; the backend restricts
    // application to those slugs only.
    const specificMode = applyScopeChecked && applyMode === "specific";
    const targetSlugs = specificMode ? applyTargetSlugs : null;
    if (specificMode && (!targetSlugs || targetSlugs.length === 0)) {
      setNewAddError("Pick at least one topic to apply the attribute to, or switch back to 'All topics under…'.");
      return;
    }
    if (chosenScope && !specificMode && chosenScope.descendants_count > 20) {
      const ok = window.confirm(
        `This will add "${label}" to ${chosenScope.descendants_count} topics under ${chosenScope.name}. Continue?`);
      if (!ok) return;
    }
    setNewBusy(true); setNewAddError("");
    try {
      await api.post(`/atlas/topics/${slug}/attributes`, {
        attribute_key: key, attribute_value: value,
        source: "advisor_custom",
        attribute_label: label, attribute_type: newType,
      });
      let extraToast = "";
      if (chosenScope) {
        const applyBody = {
          attribute_key: key, attribute_value: value,
          attribute_label: label, attribute_type: newType,
          only_missing: true,
        };
        if (targetSlugs) applyBody.target_slugs = targetSlugs;
        const r = await api.post(
          `/atlas/topics/${chosenScope.slug}/apply-attribute-to-descendants`,
          applyBody);
        const applied = r.data?.applied || 0;
        const skipped = r.data?.skipped_existing || 0;
        const scopeLbl = specificMode
          ? `${targetSlugs.length} picked topic${targetSlugs.length === 1 ? "" : "s"}`
          : `topics under ${chosenScope.name}`;
        extraToast = ` Also applied to ${applied} ${scopeLbl}` +
          (skipped ? ` (${skipped} already had a value, skipped)` : "") + ".";
      }
      toast.success(`Added '${label}' = '${value}'.${extraToast}`);
      resetAddForm();
      setAddOpen(false);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setNewAddError("This attribute already exists, edit it instead.");
      } else {
        setNewAddError(formatApiErrorDetail(detail) || "Save failed.");
      }
    } finally { setNewBusy(false); }
  };

  return (
    <div data-testid="atlas-topic-structured-attrs">
      <div className="flex items-baseline justify-between">
        <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
          Structured attributes
        </h2>
        {/* Msg 590 · Phase A — retired the entity_type pill. Post-migration
            it always displayed "Other", which was noise. */}
      </div>
      <UnifiedAttributeTable
        loading={loading}
        combinedRows={combinedRows}
        editingKey={editingKey}
        savingKey={savingKey}
        editStr={editStr} setEditStr={setEditStr}
        editBool={editBool} setEditBool={setEditBool}
        editList={editList} setEditList={setEditList}
        editListDraft={editListDraft} setEditListDraft={setEditListDraft}
        editLabel={editLabel} setEditLabel={setEditLabel}
        onStartEdit={startEdit}
        onCommit={commitEdit}
        onCancelEdit={() => setEditingKey(null)}
        onRemove={removeRow}
        isAdmin={isAtlasAdmin}
        selectedKeys={selectedKeys}
        onToggleSelect={(key) => setSelectedKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key); else next.add(key);
          return next;
        })}
        onToggleSelectAll={() => setSelectedKeys((prev) => {
          const allKeys = combinedRows.map((r) => r.attribute_key);
          if (prev.size === allKeys.length) return new Set();
          return new Set(allKeys);
        })}
      />
      {/* Bulk-select bar. Admin-only. Renders when 1+ rows selected. */}
      {isAtlasAdmin && selectedKeys.size > 0 && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-red-300 bg-red-50/60 px-4 py-2"
             data-testid="atlas-attr-bulk-bar">
          <span className="font-hand text-tb-navy text-sm" data-testid="atlas-attr-bulk-count">
            {selectedKeys.size} attribute{selectedKeys.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <button type="button"
              onClick={() => setSelectedKeys(new Set())}
              disabled={bulkBusy}
              data-testid="atlas-attr-bulk-clear"
              className="h-8 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs">
              Clear selection
            </button>
            <button type="button"
              onClick={() => { setBulkRemoveSchema(false); setBulkConfirmText(""); setBulkConfirmOpen(true); }}
              disabled={bulkBusy}
              data-testid="atlas-attr-bulk-delete"
              className="h-8 px-3 rounded-full bg-red-600 text-white font-hand text-xs hover:bg-red-700 disabled:opacity-50">
              Delete selected ({selectedKeys.size})
            </button>
          </div>
        </div>
      )}
      {bulkConfirmOpen && (
        <BulkDeleteConfirmModal
          count={selectedKeys.size}
          removeSchema={bulkRemoveSchema}
          setRemoveSchema={setBulkRemoveSchema}
          confirmText={bulkConfirmText}
          setConfirmText={setBulkConfirmText}
          busy={bulkBusy}
          onCancel={() => setBulkConfirmOpen(false)}
          onConfirm={async () => {
            if (bulkConfirmText.trim().toLowerCase() !== "yes") return;
            setBulkBusy(true);
            try {
              await api.post(`/atlas/topics/${slug}/attributes/bulk-delete`, {
                attribute_keys: Array.from(selectedKeys),
                remove_schema: bulkRemoveSchema,
              });
              toast.success(bulkRemoveSchema
                ? `Removed ${selectedKeys.size} attribute${selectedKeys.size === 1 ? "" : "s"} entirely.`
                : `Cleared ${selectedKeys.size} attribute value${selectedKeys.size === 1 ? "" : "s"}.`);
              // 2026-08-10 · Same optimistic-splice pattern as
              // singular delete so schema-driven template rows don't
              // ghost back into the panel after reload.
              const deletedSet = new Set(
                Array.from(selectedKeys).map(
                  (k) => String(k || "").trim().toLowerCase()));
              setRows((prev) => prev.filter(
                (r) => !deletedSet.has(String(r.attribute_key || "").trim().toLowerCase())));
              if (bulkRemoveSchema) {
                setSchema((prev) => ({
                  ...prev,
                  attributes: (prev.attributes || []).filter(
                    (a) => !deletedSet.has(String(a.key || "").trim().toLowerCase())),
                }));
                setHiddenKeys((prev) => {
                  const next = new Set(prev);
                  for (const k of deletedSet) next.add(k);
                  return next;
                });
              }
              setSelectedKeys(new Set());
              setBulkConfirmOpen(false);
              reload();
            } catch (e) {
              toast.error("Bulk delete failed — try again.");
            } finally {
              setBulkBusy(false);
            }
          }}
        />
      )}
      {!loading && (
        <CustomAttributeAdder
          open={addOpen}
          onOpen={() => { resetAddForm(); setAddOpen(true); }}
          onCancel={() => { setAddOpen(false); resetAddForm(); }}
          onSubmit={submitCustom}
          busy={newBusy}
          label={newLabel} setLabel={setNewLabel}
          type={newType} setType={setNewType}
          valueStr={newValueStr} setValueStr={setNewValueStr}
          valueBool={newValueBool} setValueBool={setNewValueBool}
          valueList={newValueList} setValueList={setNewValueList}
          valueListDraft={newValueListDraft} setValueListDraft={setNewValueListDraft}
          scopes={scopes}
          scopeChecked={applyScopeChecked} setScopeChecked={setApplyScopeChecked}
          scopeSlug={applyScopeSlug} setScopeSlug={setApplyScopeSlug}
          applyMode={applyMode} setApplyMode={setApplyMode}
          targetSlugs={applyTargetSlugs} setTargetSlugs={setApplyTargetSlugs}
          canSubmit={canSubmitCustom}
          errorText={newAddError}
          derivedKey={derivedNewKey}
        />
      )}
      {/* Msg 604 · Phase B — inline picker to attach a library
          attribute to this folder. Same panel as
          CustomAttributeAdder in terms of vertical placement so the
          user has BOTH affordances in the same visual band. */}
      {!loading && (
        <LibraryAttributeAdder
          slug={slug}
          onSaved={reload}
        />
      )}
      {!loading && (schema.attributes || []).length === 0 && combinedRows.length === 0 && !addOpen && (
        // Msg 590 · Phase A follow-up (Issue 2) — empty state is now
        // silent. The retired nudge ("This topic doesn't have a
        // structured schema yet — click + Add custom attribute") felt
        // like the auto-scaffold prompt the user asked us to kill.
        // Leaving the panel bare communicates the same thing without
        // hectoring.
        null
      )}
      {deleteModal && (
        <DeleteAttributeModal
          row={deleteModal.row}
          topicName={slug}
          scopes={scopes}
          scopeChecked={deleteScopeChecked} setScopeChecked={setDeleteScopeChecked}
          scopeSlug={deleteScopeSlug} setScopeSlug={setDeleteScopeSlug}
          applyMode={deleteMode} setApplyMode={setDeleteMode}
          targetSlugs={deleteTargetSlugs} setTargetSlugs={setDeleteTargetSlugs}
          busy={deleteBusy}
          onCancel={() => setDeleteModal(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}


function UnifiedAttributeTable({
  loading, combinedRows, editingKey, savingKey,
  editStr, setEditStr, editBool, setEditBool,
  editList, setEditList, editListDraft, setEditListDraft,
  editLabel, setEditLabel,
  onStartEdit, onCommit, onCancelEdit, onRemove,
  isAdmin = false, selectedKeys = new Set(),
  onToggleSelect, onToggleSelectAll,
}) {
  if (loading) {
    return (
      <div className="mt-3 rounded-xl border border-tb-navy/10 bg-tb-navy/[0.02] p-4 font-hand text-tb-navy/50 text-xs">
        Loading spec sheet…
      </div>
    );
  }
  if (combinedRows.length === 0) {
    return <p className="mt-2 font-hand text-tb-navy/50 text-xs">No structured attributes yet.</p>;
  }
  const allSelected = combinedRows.length > 0 && selectedKeys.size === combinedRows.length;
  const someSelected = selectedKeys.size > 0 && !allSelected;
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-tb-navy/15">
      <table className="w-full" data-testid="atlas-topic-attr-table">
        <thead className="bg-tb-navy/[0.04]">
          <tr>
            {isAdmin && (
              <th className="w-9 px-3 py-2">
                <input type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={onToggleSelectAll}
                  data-testid="atlas-attr-select-all"
                  className="h-3.5 w-3.5 accent-red-600 cursor-pointer" />
              </th>
            )}
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Attribute</th>
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Value</th>
            <th className="w-24 px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {combinedRows.map((row) => (
            <UnifiedAttributeRow key={row.attribute_key} row={row}
              editing={editingKey === row.attribute_key}
              busy={savingKey === row.attribute_key}
              editStr={editStr} setEditStr={setEditStr}
              editBool={editBool} setEditBool={setEditBool}
              editList={editList} setEditList={setEditList}
              editListDraft={editListDraft} setEditListDraft={setEditListDraft}
              editLabel={editLabel} setEditLabel={setEditLabel}
              onStartEdit={onStartEdit}
              onCommit={onCommit}
              onCancelEdit={onCancelEdit}
              onRemove={onRemove}
              isAdmin={isAdmin}
              selected={selectedKeys.has(row.attribute_key)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function BulkDeleteConfirmModal({ count, removeSchema, setRemoveSchema,
                                    confirmText, setConfirmText,
                                    busy, onCancel, onConfirm }) {
  const canConfirm = confirmText.trim().toLowerCase() === "yes" && !busy;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="atlas-attr-bulk-confirm-modal"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
        <div>
          <div className="font-serif text-tb-navy text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>
            Delete {count} attribute{count === 1 ? "" : "s"}?
          </div>
          <p className="mt-1 font-hand text-xs text-tb-navy/60">
            This action affects only this folder — global attributes and other folders are untouched.
          </p>
        </div>
        <div className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="bulk-mode"
              checked={!removeSchema}
              onChange={() => setRemoveSchema(false)}
              data-testid="atlas-attr-bulk-mode-clear"
              className="mt-1 accent-tb-navy" />
            <span>
              <span className="font-hand text-sm text-tb-navy font-semibold">Clear values only</span>
              <span className="block font-hand text-[11px] text-tb-navy/60">
                Removes fact values. Template slots stay visible for re-filling.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="bulk-mode"
              checked={removeSchema}
              onChange={() => setRemoveSchema(true)}
              data-testid="atlas-attr-bulk-mode-remove"
              className="mt-1 accent-red-600" />
            <span>
              <span className="font-hand text-sm text-red-700 font-semibold">Remove attributes entirely</span>
              <span className="block font-hand text-[11px] text-tb-navy/60">
                Deletes fact values AND removes the template slots from this folder's schema.
              </span>
            </span>
          </label>
        </div>
        <div>
          <label className="block font-hand text-[11px] uppercase tracking-widest text-tb-navy/60">
            Type <span className="text-red-600 font-bold">yes</span> to confirm
          </label>
          <input type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={busy}
            autoFocus
            data-testid="atlas-attr-bulk-confirm-input"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-tb-navy/25 outline-none focus:border-red-600 font-hand text-sm text-tb-navy" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="atlas-attr-bulk-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={!canConfirm}
            data-testid="atlas-attr-bulk-confirm-submit"
            className="h-9 px-4 rounded-full bg-red-600 text-white font-hand text-sm disabled:opacity-40 hover:bg-red-700">
            {busy ? "Deleting…" : `Delete ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function UnifiedAttributeRow({
  row, editing, busy,
  editStr, setEditStr, editBool, setEditBool,
  editList, setEditList, editListDraft, setEditListDraft,
  editLabel, setEditLabel,
  onStartEdit, onCommit, onCancelEdit, onRemove,
  isAdmin = false, selected = false, onToggleSelect,
}) {
  const def = row.def;
  const label = def?.label || humanize(row.attribute_key);
  const testKey = row.attribute_key;
  const showLabelEditor = editing && row.custom;
  return (
    <tr data-testid={`atlas-topic-attr-row-${testKey}`}
        data-empty={row.empty ? "1" : "0"}
        data-selected={selected ? "1" : "0"}
        className={"border-t border-tb-navy/10 " + (selected ? "bg-red-50/40" : "")}>
      {isAdmin && (
        <td className="w-9 px-3 py-2">
          <input type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect?.(row.attribute_key)}
            data-testid={`atlas-attr-select-${testKey}`}
            className="h-3.5 w-3.5 accent-red-600 cursor-pointer" />
        </td>
      )}
      <td className="px-3 py-2 font-hand text-[12px] text-tb-navy/80 uppercase tracking-wider">
        {showLabelEditor ? (
          <input value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            data-testid={`atlas-topic-attr-label-input-${testKey}`}
            className="w-full h-8 px-2 rounded border border-tb-navy/25 font-hand text-xs text-tb-navy uppercase tracking-wider" />
        ) : label}
      </td>
      <td className="px-3 py-2 font-hand text-sm text-tb-navy">
        {editing ? (
          <UnifiedAttributeInlineEdit
            row={row} busy={busy}
            editStr={editStr} setEditStr={setEditStr}
            editBool={editBool} setEditBool={setEditBool}
            editList={editList} setEditList={setEditList}
            editListDraft={editListDraft} setEditListDraft={setEditListDraft}
            onCommit={onCommit} onCancel={onCancelEdit}
          />
        ) : row.empty ? (
          <button type="button" onClick={() => onStartEdit(row)}
            data-testid={`atlas-topic-attr-fill-${testKey}`}
            className="group inline-flex items-center gap-1.5 w-full h-8 px-2 rounded-md border border-dashed border-tb-navy/30 text-tb-navy/50 hover:border-tb-navy hover:text-tb-navy hover:bg-tb-navy/5 font-hand text-xs transition-colors"
            title="Click to fill">
            <Pencil className="w-3 h-3 opacity-70 group-hover:opacity-100" />
            <span>Click to fill</span>
          </button>
        ) : (
          <button type="button" onClick={() => onStartEdit(row)}
            className="text-left hover:underline underline-offset-2 decoration-tb-navy/30 w-full"
            title="Click to edit">{row.attribute_value}</button>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {!editing && (
          <div className="inline-flex items-center gap-1">
            {/* K1 (2026-08-05) — hidden entirely when the underlying
                fact has no source metadata; keeps schema-only empty
                rows unchanged. */}
            <FactSourceInfoBadge fact={row} testId={`attr-${testKey}`} />
            <button type="button" onClick={() => onStartEdit(row)} aria-label="Edit"
              data-testid={`atlas-topic-attr-edit-${testKey}`}
              className="h-7 w-7 inline-flex items-center justify-center rounded text-tb-navy/60 hover:bg-tb-navy/5 hover:text-tb-navy">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {/* (2026-08-04) Trash icon is now visible on empty rows too.
                Previously gated behind `!row.empty` — meaning attributes
                added via Folder Presets (which start empty) had no way
                to be removed from an individual folder short of editing
                the Preset itself. The backend endpoint
                `DELETE /api/atlas/topics/{slug}/attributes/{key}` now
                handles both fact-backed and schema-only rows and is
                fully idempotent. */}
            <button type="button" onClick={() => onRemove(row)} disabled={busy}
              aria-label={row.empty ? "Remove attribute from this folder" : "Delete"}
              title={row.empty
                ? "Remove this attribute from this folder (won't affect the library or other folders)"
                : "Delete this attribute's value"}
              data-testid={`atlas-topic-attr-remove-${testKey}`}
              className="h-7 w-7 inline-flex items-center justify-center rounded text-red-700/70 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}


function UnifiedAttributeInlineEdit({
  row, busy,
  editStr, setEditStr, editBool, setEditBool,
  editList, setEditList, editListDraft, setEditListDraft,
  onCommit, onCancel,
}) {
  const def = row.def || { type: "string" };
  const testKey = row.attribute_key;
  return (
    <div className="flex items-start gap-1.5">
      <div className="flex-1 min-w-0">
        {def.type === "bool" ? (
          <label className="inline-flex items-center gap-2 h-8 px-3 rounded border border-tb-navy/25 bg-white cursor-pointer">
            <input type="checkbox" checked={editBool}
              onChange={(e) => setEditBool(e.target.checked)}
              className="accent-tb-navy"
              data-testid={`atlas-topic-attr-input-bool-${testKey}`} />
            <span className="font-hand text-sm text-tb-navy">{editBool ? "Yes" : "No"}</span>
          </label>
        ) : def.type === "list" ? (
          <div className="rounded border border-tb-navy/25 bg-white p-1.5 min-h-[32px]">
            <div className="flex flex-wrap gap-1 items-center">
              {editList.map((chip, i) => (
                <span key={i} className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-tb-navy/10 font-hand text-xs text-tb-navy">
                  {chip}
                  <button type="button"
                    onClick={() => setEditList((prev) => prev.filter((_, j) => j !== i))}
                    className="text-tb-navy/60 hover:text-tb-navy">
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input value={editListDraft} onChange={(e) => setEditListDraft(e.target.value)}
                data-testid={`atlas-topic-attr-input-list-${testKey}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editListDraft.trim()) {
                    e.preventDefault();
                    setEditList((prev) => [...prev, editListDraft.trim()]);
                    setEditListDraft("");
                  } else if (e.key === "Escape") { onCancel(); }
                }}
                placeholder={editList.length === 0 ? "Type a value, press Enter" : "+ Add"}
                className="flex-1 min-w-[100px] h-6 outline-none font-hand text-sm text-tb-navy" />
            </div>
          </div>
        ) : (
          <input value={editStr} onChange={(e) => setEditStr(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommit(row);
              else if (e.key === "Escape") onCancel();
            }}
            placeholder=""
            data-testid={`atlas-topic-attr-input-str-${testKey}`}
            className="w-full h-8 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy"
            autoFocus />
        )}
      </div>
      <button type="button" onClick={() => onCommit(row)} disabled={busy}
        data-testid={`atlas-topic-attr-commit-${testKey}`}
        className="h-8 w-8 inline-flex items-center justify-center rounded bg-tb-navy text-tb-gold-bright">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
      <button type="button" onClick={onCancel}
        data-testid={`atlas-topic-attr-cancel-${testKey}`}
        className="h-8 w-8 inline-flex items-center justify-center rounded border border-tb-navy/20 text-tb-navy/60">
        <CloseIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}



// Msg 574 · UX-Cleanup — Inline "+ Add custom attribute" affordance
// below the schema-driven attribute table. When the checkbox is
// ticked, the attribute is ALSO applied to every descendant under the
// selected ancestor scope (never overwrites existing values).
function CustomAttributeAdder({
  open, onOpen, onCancel, onSubmit, busy,
  label, setLabel, type, setType,
  valueStr, setValueStr, valueBool, setValueBool,
  valueList, setValueList, valueListDraft, setValueListDraft,
  scopes, scopeChecked, setScopeChecked, scopeSlug, setScopeSlug,
  applyMode, setApplyMode, targetSlugs, setTargetSlugs,
  canSubmit, errorText, derivedKey,
}) {
  if (!open) {
    return (
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={onOpen}
          data-testid="atlas-topic-attr-add-custom-open"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Plus className="w-3.5 h-3.5" /> Add custom attribute
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl border border-tb-navy/20 bg-white p-4"
         data-testid="atlas-topic-attr-add-custom-form">
      <div className="flex items-center justify-between mb-3">
        <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
          Add custom attribute
        </div>
        <div className="flex items-center gap-1">
          {/* Msg 590 · Phase A follow-up (Issue 4) — jump into Presets
              with this attribute pre-filled so admins can apply it in
              bulk (by folder + descendants OR by category). */}
          <button type="button"
            data-testid="atlas-topic-attr-add-bulk-apply"
            title={(label || "").trim()
              ? "Open Folder Presets with this attribute pre-filled"
              : "Type a label first to enable bulk apply"}
            disabled={!(label || "").trim()}
            onClick={() => {
              const payload = {
                name: (label || "").trim(),
                answer_type: type || "string",
                options: type === "list" ? (valueList || []) : [],
              };
              const url = `/atlas/admin/presets?prefill_attribute=${encodeURIComponent(JSON.stringify(payload))}`;
              window.open(url, "_blank", "noopener");
            }}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-hand uppercase tracking-widest bg-tb-navy/5 hover:bg-tb-navy/10 text-tb-navy/80 disabled:opacity-40">
            Bulk apply →
          </button>
          <button type="button" onClick={onCancel}
            data-testid="atlas-topic-attr-add-custom-cancel"
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-tb-navy/5 text-tb-navy/60">
            <CloseIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Ship Class)"
          data-testid="atlas-topic-attr-add-label"
          className="md:col-span-5 h-9 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
        <select value={type} onChange={(e) => setType(e.target.value)}
          data-testid="atlas-topic-attr-add-type"
          className="md:col-span-2 h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-xs text-tb-navy">
          <option value="string">Text</option>
          <option value="number">Number</option>
          <option value="bool">Yes / No</option>
          <option value="list">List of items</option>
          <option value="link">Link (URL)</option>
          <option value="date">Date</option>
        </select>
        <div className="md:col-span-5">
          {type === "bool" ? (
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded border border-tb-navy/25 bg-white cursor-pointer w-full">
              <input type="checkbox" checked={valueBool}
                onChange={(e) => setValueBool(e.target.checked)}
                data-testid="atlas-topic-attr-add-value-bool"
                className="accent-tb-navy" />
              <span className="font-hand text-sm text-tb-navy">{valueBool ? "Yes" : "No"}</span>
            </label>
          ) : type === "list" ? (
            <div className="rounded border border-tb-navy/25 bg-white p-1.5 min-h-[36px]">
              <div className="flex flex-wrap gap-1 items-center">
                {valueList.map((chip, i) => (
                  <span key={i} className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-tb-navy/10 font-hand text-xs text-tb-navy">
                    {chip}
                    <button type="button"
                      onClick={() => setValueList(valueList.filter((_, j) => j !== i))}
                      className="text-tb-navy/60 hover:text-tb-navy">
                      <CloseIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input value={valueListDraft}
                  onChange={(e) => setValueListDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (valueListDraft || "").trim()) {
                      e.preventDefault();
                      setValueList([...valueList, valueListDraft.trim()]);
                      setValueListDraft("");
                    }
                  }}
                  data-testid="atlas-topic-attr-add-value-list"
                  placeholder={valueList.length === 0 ? "Value, press Enter" : "+ Add"}
                  className="flex-1 min-w-[100px] h-6 outline-none font-hand text-sm text-tb-navy" />
              </div>
            </div>
          ) : (
            <input value={valueStr} onChange={(e) => setValueStr(e.target.value)}
              placeholder="Value"
              data-testid="atlas-topic-attr-add-value-str"
              className="w-full h-9 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
          )}
        </div>
      </div>
      {scopes.length > 0 && (
        <div className="mt-3 rounded-lg border border-tb-navy/15 p-3">
          <label className="inline-flex items-center gap-2 cursor-pointer font-hand text-xs text-tb-navy/80">
            <input type="checkbox" checked={scopeChecked}
              onChange={(e) => setScopeChecked(e.target.checked)}
              data-testid="atlas-topic-attr-add-scope-check"
              className="accent-tb-navy" />
            Also apply this attribute to other topics
          </label>
          {scopeChecked && (
            <div className="mt-3 space-y-2">
              {/* Msg 582 · L.2 — mode radio: "All" vs "Specific". */}
              <div className="flex items-center gap-4 flex-wrap font-hand text-xs text-tb-navy/80">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="apply-mode" value="all"
                    checked={applyMode === "all"}
                    onChange={() => setApplyMode("all")}
                    data-testid="atlas-topic-attr-add-mode-all"
                    className="accent-tb-navy" />
                  All topics under…
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="apply-mode" value="specific"
                    checked={applyMode === "specific"}
                    onChange={() => setApplyMode("specific")}
                    data-testid="atlas-topic-attr-add-mode-specific"
                    className="accent-tb-navy" />
                  Specific topics I pick
                </label>
              </div>
              <div className="flex items-center gap-2 flex-wrap font-hand text-xs text-tb-navy/70">
                <span>Scope:</span>
                <select value={scopeSlug} onChange={(e) => { setScopeSlug(e.target.value); setTargetSlugs && setTargetSlugs([]); }}
                  data-testid="atlas-topic-attr-add-scope-select"
                  className="h-8 px-2 rounded border border-tb-navy/25 bg-white font-hand text-xs text-tb-navy">
                  {scopes.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name} ({s.descendants_count} topics)
                    </option>
                  ))}
                </select>
              </div>
              {applyMode === "specific" && (
                <TopicMultiSelect
                  scopeSlug={scopeSlug}
                  selected={targetSlugs || []}
                  onChange={setTargetSlugs}
                  testId="atlas-topic-attr-add-multiselect"
                />
              )}
            </div>
          )}
        </div>
      )}
      {derivedKey && (
        <div className="mt-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/40">
          Stored as: <code>{derivedKey}</code>
        </div>
      )}
      {errorText && (
        <div className="mt-2 rounded-lg border border-red-300 bg-red-50 p-2 font-hand text-xs text-red-800"
             data-testid="atlas-topic-attr-add-error">
          {errorText}
        </div>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy}
          className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
          Cancel
        </button>
        <button type="button" onClick={onSubmit} disabled={!canSubmit || busy}
          data-testid="atlas-topic-attr-add-submit"
          className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Msg 604 · Phase B — Compact "Add from library" picker on any
// folder's attribute panel. Searchable dropdown of every
// atlas_attributes entry; picking one reveals a single value input
// (typed to the attribute's `answer_type`) and submits via
// `POST /atlas/facts` with `attribute_id + attribute_value`. The
// resulting fact carries a stable library ref so a rename in the
// library propagates to this folder immediately.
// ─────────────────────────────────────────────────────────────
function LibraryAttributeAdder({ slug, onSaved }) {
  const library = useAtlasAttributes();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return library.slice(0, 30);
    return library.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.key || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [library, search]);

  const reset = () => {
    setOpen(false); setSearch(""); setPicked(null); setValue("");
  };
  const submit = async () => {
    if (!picked) return;
    const val = String(value || "").trim();
    if (!val) { toast.error("Enter a value."); return; }
    setBusy(true);
    try {
      await api.post("/atlas/facts", {
        text: `${picked.name}: ${val}`.slice(0, 480),
        topic_slugs: [slug],
        source: "advisor_typed",
        attribute_id: picked.id,
        attribute_value: val,
      });
      toast.success(`Filed "${picked.name}" on this folder.`);
      reset();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mt-2 flex justify-end">
        <button type="button" onClick={() => setOpen(true)}
          data-testid="atlas-topic-attr-add-from-library"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Plus className="w-3.5 h-3.5" /> Add from library
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl border border-tb-navy/20 bg-white p-4 space-y-3"
         data-testid="atlas-topic-attr-add-library-panel">
      <div className="flex items-center justify-between">
        <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
          Add from library {library.length ? `(${library.length} entries)` : ""}
        </div>
        <button type="button" onClick={reset}
          data-testid="atlas-topic-attr-add-library-cancel"
          className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-tb-navy/5 text-tb-navy/60">
          <CloseIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      {!picked && (
        <>
          <input type="text" autoFocus value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the library…"
            data-testid="atlas-topic-attr-add-library-search"
            className="w-full h-9 px-3 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy" />
          <div className="max-h-56 overflow-y-auto rounded-lg divide-y divide-tb-navy/5 bg-tb-navy/[0.02]"
               data-testid="atlas-topic-attr-add-library-list">
            {filtered.length === 0 && (
              <div className="p-3 text-xs font-hand text-tb-navy/50 text-center">
                {library.length === 0
                  ? "Library is empty. Create entries in Admin → Attributes."
                  : "No matches."}
              </div>
            )}
            {filtered.map((a) => (
              <button key={a.id} type="button"
                onClick={() => { setPicked(a); setValue(a.answer_type === "bool" ? "true" : ""); }}
                data-testid={`atlas-topic-attr-add-library-pick-${a.id}`}
                className="w-full text-left px-3 py-2 hover:bg-tb-navy/5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-tb-navy font-semibold truncate">{a.name}</div>
                  <div className="text-[10px] font-mono text-tb-navy/50">
                    {a.key} · {a.answer_type}
                    {a.answer_type === "list" && (a.options || []).length
                      ? ` · ${(a.options || []).length} options` : ""}
                  </div>
                </div>
                <Plus className="w-3.5 h-3.5 text-tb-navy/60 shrink-0" />
              </button>
            ))}
          </div>
        </>
      )}
      {picked && (
        <div className="space-y-2">
          <div className="font-hand text-xs text-tb-navy/70">
            <b className="text-tb-navy">{picked.name}</b>{" "}
            <span className="text-tb-navy/40">({picked.answer_type})</span>
          </div>
          {picked.answer_type === "list" && (picked.options || []).length > 0 ? (
            <select value={value} onChange={(e) => setValue(e.target.value)}
              data-testid="atlas-topic-attr-add-library-value-list"
              className="w-full h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
              <option value="">— pick a value —</option>
              {(picked.options || []).map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : picked.answer_type === "bool" ? (
            <select value={value} onChange={(e) => setValue(e.target.value)}
              data-testid="atlas-topic-attr-add-library-value-bool"
              className="w-full h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <input
              type={picked.answer_type === "number" ? "number"
                    : picked.answer_type === "date" ? "date" : "text"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              data-testid="atlas-topic-attr-add-library-value"
              placeholder={picked.description || `Value for ${picked.name}`}
              className="w-full h-9 px-3 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => setPicked(null)} disabled={busy}
              data-testid="atlas-topic-attr-add-library-back"
              className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50">
              Back
            </button>
            <button type="button" onClick={submit}
              disabled={busy || !String(value || "").trim()}
              data-testid="atlas-topic-attr-add-library-submit"
              className="h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-40 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              File value
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Msg 576 · Delete-with-siblings modal. Mirrors the Msg-574 add-flow.
// Msg 582 · L.2 — now supports "specific topics" cherry-pick mode.
function DeleteAttributeModal({
  row, topicName, scopes, scopeChecked, setScopeChecked,
  scopeSlug, setScopeSlug,
  applyMode, setApplyMode, targetSlugs, setTargetSlugs,
  busy, onCancel, onConfirm,
}) {
  const label = row?.def?.label || humanize(row?.attribute_key || "");
  // (2026-08-04) — the modal now supports two cases:
  //   1) row.empty === true  → attribute row has no value; deleting
  //      removes the schema/preset entry from THIS folder only. The
  //      row itself disappears from the panel.
  //   2) row.empty === false → attribute row has a value; deleting
  //      clears the value (fact removed). The attribute definition
  //      stays and the row reverts to "Click to fill".
  const isEmptyRow = !!row?.empty;
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-topic-attr-delete-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15 p-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-tb-navy text-lg font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}>
          {isEmptyRow
            ? `Remove "${label}" from this folder?`
            : `Delete "${label}" value?`}
        </h2>
        <p className="mt-1 font-hand text-tb-navy/60 text-xs">
          {isEmptyRow ? (
            <>
              Removes <b>{label}</b> from <b>{topicName}</b> only.
              This won't affect other folders or the attribute library.
            </>
          ) : (
            <>
              Removes the value from <b>{topicName}</b>. The attribute
              definition stays intact — the row will revert to "Click to fill".
            </>
          )}
        </p>
        {scopes.length > 0 && (
          <div className="mt-4 rounded-lg border border-tb-navy/15 p-3">
            <label className="inline-flex items-center gap-2 cursor-pointer font-hand text-xs text-tb-navy/80">
              <input type="checkbox" checked={scopeChecked}
                onChange={(e) => setScopeChecked(e.target.checked)}
                data-testid="atlas-topic-attr-delete-scope-check"
                className="accent-tb-navy" />
              Also remove this attribute value from other topics
            </label>
            {scopeChecked && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-4 flex-wrap font-hand text-xs text-tb-navy/80">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="delete-mode" value="all"
                      checked={applyMode === "all"}
                      onChange={() => setApplyMode("all")}
                      data-testid="atlas-topic-attr-delete-mode-all"
                      className="accent-tb-navy" />
                    All topics under…
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="delete-mode" value="specific"
                      checked={applyMode === "specific"}
                      onChange={() => setApplyMode("specific")}
                      data-testid="atlas-topic-attr-delete-mode-specific"
                      className="accent-tb-navy" />
                    Specific topics I pick
                  </label>
                </div>
                <div className="flex items-center gap-2 flex-wrap font-hand text-xs text-tb-navy/70">
                  <span>Scope:</span>
                  <select value={scopeSlug}
                    onChange={(e) => { setScopeSlug(e.target.value); setTargetSlugs && setTargetSlugs([]); }}
                    data-testid="atlas-topic-attr-delete-scope-select"
                    className="h-8 px-2 rounded border border-tb-navy/25 bg-white font-hand text-xs text-tb-navy">
                    {scopes.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.name} ({s.descendants_count} topics)
                      </option>
                    ))}
                  </select>
                </div>
                {applyMode === "specific" && (
                  <TopicMultiSelect
                    scopeSlug={scopeSlug}
                    selected={targetSlugs || []}
                    onChange={setTargetSlugs}
                    testId="atlas-topic-attr-delete-multiselect"
                  />
                )}
              </div>
            )}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="atlas-topic-attr-delete-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
            data-testid="atlas-topic-attr-delete-confirm"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-red-700 text-white font-hand text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Msg 582 · L.2 — Topic multi-select. Lists all descendants of a
// scope with a search filter and a running count. Selection state
// lifted to caller so both Add + Delete flows can reuse it.
// ─────────────────────────────────────────────────────────────
function TopicMultiSelect({ scopeSlug, selected, onChange, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!scopeSlug) { setItems([]); return; }
    setLoading(true); setErr("");
    api.get(`/atlas/topics/${scopeSlug}/descendants-list`)
      .then((r) => setItems(r.data?.descendants || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load topics."))
      .finally(() => setLoading(false));
  }, [scopeSlug]);
  const filtered = useMemo(() => {
    const qn = (q || "").trim().toLowerCase();
    if (!qn) return items;
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(qn) ||
      (it.slug || "").toLowerCase().includes(qn));
  }, [items, q]);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const toggle = (slug) => {
    const next = new Set(selectedSet);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    onChange(Array.from(next));
  };
  const toggleAllVisible = () => {
    const visibleSlugs = filtered.map((f) => f.slug);
    const allSelected = visibleSlugs.every((s) => selectedSet.has(s));
    const next = new Set(selectedSet);
    if (allSelected) {
      for (const s of visibleSlugs) next.delete(s);
    } else {
      for (const s of visibleSlugs) next.add(s);
    }
    onChange(Array.from(next));
  };
  const overSoftLimit = (selected || []).length > 200;
  return (
    <div className="mt-1 rounded-lg border border-tb-navy/15 bg-white"
         data-testid={testId}>
      <div className="flex items-center gap-2 p-2 border-b border-tb-navy/10">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter topics…"
          data-testid={`${testId}-filter`}
          className="flex-1 h-8 px-2 rounded border border-tb-navy/20 font-hand text-xs text-tb-navy"
        />
        <button type="button" onClick={toggleAllVisible}
          disabled={filtered.length === 0}
          data-testid={`${testId}-toggle-visible`}
          className="h-8 px-2 rounded border border-tb-navy/20 font-hand text-[11px] text-tb-navy hover:bg-tb-navy/5 disabled:opacity-40">
          Toggle visible
        </button>
      </div>
      <div className="max-h-60 overflow-y-auto p-1" data-testid={`${testId}-list`}>
        {loading && (
          <div className="p-3 font-hand text-[11px] text-tb-navy/50">Loading…</div>
        )}
        {!loading && err && (
          <div className="p-3 font-hand text-[11px] text-red-700">{err}</div>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="p-3 font-hand text-[11px] text-tb-navy/50">
            {items.length === 0 ? "No topics under this scope." : "No topics match the filter."}
          </div>
        )}
        {!loading && !err && filtered.map((it) => {
          const checked = selectedSet.has(it.slug);
          return (
            <label key={it.slug}
              data-testid={`${testId}-row-${it.slug}`}
              className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm hover:bg-tb-navy/5 ${checked ? "bg-tb-navy/[0.06]" : ""}`}>
              <input type="checkbox" checked={checked}
                onChange={() => toggle(it.slug)}
                className="accent-tb-navy shrink-0" />
              <span className="font-hand text-xs text-tb-navy truncate flex-1">{it.name}</span>
              {it.entity_type && it.entity_type !== "other" && (
                <span className="font-hand text-[9px] uppercase tracking-widest text-tb-navy/40 shrink-0">
                  {humanize(it.entity_type)}
                </span>
              )}
            </label>
          );
        })}
      </div>
      <div className={`px-3 py-2 border-t border-tb-navy/10 flex items-center justify-between font-hand text-[11px] ${overSoftLimit ? "text-red-700" : "text-tb-navy/70"}`}>
        <span data-testid={`${testId}-count`}>
          {(selected || []).length} of {items.length} selected
        </span>
        {overSoftLimit && <span>(over 200 — consider using "All topics under…")</span>}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// Msg 595 · Bulk set-category modal — invoked from the multi-select
// action bar. Reads user-defined categories from the shared cache;
// picks land in one `POST /topics/bulk/set-category` call.
// ═══════════════════════════════════════════════════════════════
function BulkSetCategoryModal({ slugs, onClose, onDone }) {
  const categories = useAtlasCategories();
  const [choice, setChoice] = useState("");   // "" = None (clear)
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/atlas/topics/bulk/set-category", {
        slugs, category_id: choice || null,
      });
      const d = r.data || {};
      toast.success(`Category applied — ${d.updated_count} folder${d.updated_count === 1 ? "" : "s"} updated${d.skipped_missing_slugs?.length ? `, ${d.skipped_missing_slugs.length} skipped` : ""}.`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk set-category failed.");
    } finally { setBusy(false); }
  };
  const Preview = choice
    ? iconByName((categories.find((c) => c.id === choice) || {}).icon_name)
    : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-tree-bulk-set-category-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-6 pt-5 pb-3 border-b border-tb-navy/10">
          <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: `"Playfair Display", serif` }}>
            Set category on {slugs.length} folder{slugs.length === 1 ? "" : "s"}
          </h2>
        </div>
        <div className="p-6 space-y-3">
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Category</span>
            <div className="mt-1 flex items-center gap-2">
              {Preview && (
                <span className="w-9 h-9 rounded-full bg-tb-navy/[0.06] flex items-center justify-center">
                  <Preview className="w-4 h-4 text-tb-navy" />
                </span>
              )}
              <select value={choice} onChange={(e) => setChoice(e.target.value)}
                data-testid="atlas-tree-bulk-set-category-select"
                className="flex-1 h-10 px-3 rounded-lg border border-tb-navy/20 bg-white text-sm text-tb-navy outline-none focus:border-tb-navy">
                <option value="">— None (clear category) —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            {categories.length === 0 && (
              <span className="mt-2 block text-[11px] font-hand text-tb-navy/50">
                No categories yet. Create some in <b>Admin → Categories</b>.
              </span>
            )}
          </label>
          <p className="font-hand text-[11px] text-tb-navy/50">
            Only the category tag is updated. Folders are not moved, renamed or deleted.
          </p>
        </div>
        <div className="px-6 pb-5 pt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="atlas-tree-bulk-set-category-cancel"
            className="h-10 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="atlas-tree-bulk-set-category-confirm"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// (2026-08-04) Repair Orphans Modal
// ─────────────────────────────────────────────────────────────
// Fetches dry-run suggestions from
// `POST /api/atlas/admin/topics/repair-orphan-parents?dry_run=true`
// then lets the admin review and commit. High-confidence rows are
// pre-checked; medium/low are read-only (labeled "manual review
// needed"). On Commit, POSTs with `dry_run=false` — the backend
// commits ONLY confidence:"high" matches regardless of what the UI
// sends, so this is defense-in-depth.
function RepairOrphansModal({ onClose, onCommitted }) {
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [selectedHigh, setSelectedHigh] = useState(new Set());
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        "/atlas/admin/topics/repair-orphan-parents?dry_run=true");
      setDryRun(data);
      // pre-select all high-confidence orphans
      setSelectedHigh(new Set((data.high_confidence || []).map((s) => s.orphan_slug)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to scan for orphans.");
      setDryRun({ high_confidence: [], medium_confidence: [], low_confidence: [] });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const commit = async () => {
    if (committing) return;
    setCommitting(true);
    try {
      const { data } = await api.post(
        "/atlas/admin/topics/repair-orphan-parents?dry_run=false");
      const n = (data.committed || []).length;
      const errs = (data.errors || []).length;
      if (errs) {
        toast.error(`Reparented ${n} orphan${n === 1 ? "" : "s"}, ${errs} error${errs === 1 ? "" : "s"}.`);
      } else if (n > 0) {
        toast.success(`Reparented ${n} orphan sub-folder${n === 1 ? "" : "s"}.`);
      } else {
        toast.success("Nothing to commit — no high-confidence matches.");
      }
      onCommitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Commit failed.");
      setCommitting(false);
    }
  };

  const high = dryRun?.high_confidence || [];
  const medium = dryRun?.medium_confidence || [];
  const low = dryRun?.low_confidence || [];
  const total = high.length + medium.length + low.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start md:items-center justify-center overflow-y-auto p-4"
         data-testid="atlas-repair-orphans-modal"
         onClick={(e) => { if (e.target === e.currentTarget && !committing) onClose(); }}>
      <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl border border-tb-navy/15 my-4">
        <div className="p-5 border-b border-tb-navy/10 flex items-center justify-between">
          <div>
            <h3 className="text-tb-navy text-xl font-semibold flex items-center gap-2"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Repair orphan sub-folders
            </h3>
            <p className="mt-1 font-hand text-tb-navy/60 text-xs">
              {loading ? "Scanning…" : (
                <>Found <b>{total}</b> orphan{total === 1 ? "" : "s"}
                  {" — "}
                  <b>{high.length}</b> auto-repairable ·{" "}
                  <b>{medium.length + low.length}</b> need manual review.</>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={committing}
            data-testid="atlas-repair-orphans-close"
            className="h-8 w-8 rounded-full hover:bg-tb-navy/5 inline-flex items-center justify-center text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto space-y-4">
          {loading && (
            <div className="text-center font-hand text-tb-navy/50 py-6">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Scanning tree for ghost parents…
            </div>
          )}
          {!loading && high.length > 0 && (
            <section data-testid="atlas-repair-orphans-high-section">
              <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-2">
                High confidence — {high.length}
              </div>
              <div className="space-y-1.5">
                {high.map((s) => {
                  const checked = selectedHigh.has(s.orphan_slug);
                  return (
                    <label key={s.orphan_slug}
                      data-testid={`atlas-repair-orphans-high-row-${s.orphan_slug}`}
                      className={
                        "flex items-start gap-2 p-2 rounded-lg border cursor-pointer " +
                        (checked
                          ? "border-tb-navy/40 bg-tb-navy/5"
                          : "border-tb-navy/15 bg-white")
                      }>
                      <input type="checkbox" checked={checked}
                        onChange={(e) => {
                          const next = new Set(selectedHigh);
                          if (e.target.checked) next.add(s.orphan_slug);
                          else next.delete(s.orphan_slug);
                          setSelectedHigh(next);
                        }}
                        data-testid={`atlas-repair-orphans-check-${s.orphan_slug}`}
                        className="mt-1" />
                      <div className="flex-1 min-w-0 font-hand text-xs">
                        <div className="text-tb-navy font-semibold truncate">
                          {s.orphan_slug}
                        </div>
                        <div className="text-tb-navy/70 mt-0.5">
                          <span className="text-red-700/70 line-through">{s.ghost_parent}</span>
                          {" → "}
                          <span className="text-tb-navy font-semibold">
                            {s.suggested_new_parent_name || s.suggested_new_parent}
                          </span>
                          <span className="text-tb-navy/50"> ({s.suggested_new_parent})</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          )}
          {!loading && (medium.length > 0 || low.length > 0) && (
            <section data-testid="atlas-repair-orphans-manual-section">
              <div className="text-[10px] font-hand uppercase tracking-widest text-amber-700 mb-2">
                Manual review needed — {medium.length + low.length}
              </div>
              <div className="space-y-1.5">
                {[...medium, ...low].map((s) => (
                  <div key={s.orphan_slug}
                    data-testid={`atlas-repair-orphans-manual-row-${s.orphan_slug}`}
                    className="p-2 rounded-lg border border-amber-200 bg-amber-50/60 font-hand text-xs">
                    <div className="text-tb-navy font-semibold truncate">{s.orphan_slug}</div>
                    <div className="text-tb-navy/70 mt-0.5">
                      ghost <span className="text-red-700/70">{s.ghost_parent}</span>
                      {" · "}
                      {s.suggested_new_parent
                        ? <>maybe <b>{s.suggested_new_parent_name || s.suggested_new_parent}</b> ({s.confidence})</>
                        : <>no match found</>}
                    </div>
                    <div className="text-tb-navy/50 text-[10px] mt-0.5">{s.reason}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {!loading && total === 0 && (
            <div className="rounded-xl border border-dashed border-tb-navy/20 bg-white p-6 text-center font-hand text-tb-navy/60 text-sm">
              <Check className="w-5 h-5 inline mr-1 text-tb-navy/50" />
              No orphan sub-folders — tree is healthy.
            </div>
          )}
        </div>
        <div className="p-4 border-t border-tb-navy/10 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] font-hand text-tb-navy/55">
            Only high-confidence matches are committed. Manual-review rows stay listed.
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={committing}
              data-testid="atlas-repair-orphans-cancel"
              className="h-10 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
              Cancel
            </button>
            <button type="button" onClick={commit}
              disabled={committing || loading || selectedHigh.size === 0}
              data-testid="atlas-repair-orphans-commit"
              className="h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50 inline-flex items-center gap-1.5">
              {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {committing ? "Reparenting…" : `Commit ${selectedHigh.size} reparent${selectedHigh.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
